// Centralized config for the hashed share-link feature (PFM-2547).
// All knowledge of the share-link URL shape and redemption endpoint lives here
// so we can pivot when backend lands without hunting through the codebase.

// Share URLs encode the target content directly, since /share-hash/authenticate
// only returns a bearer token (no scope info). Course documents append ?courseId=…
// to keep the path shape uniform across kinds.
export const SHARE_ROUTE_PATH = '/share/:hash/:kind(assessment|task|document)/:id';

// Backend endpoints (PFM-1074). authenticate is anonymous and lives OUTSIDE
// the JSON:API server; hashed reads use the dedicated /sharehash/v1 namespace.
export const AUTHENTICATE_ENDPOINT = 'share-hash/authenticate';
export const SHARE_HASH_API_NAMESPACE = 'sharehash/v1/';

export const SHARE_KINDS = ['assessment', 'task', 'document'];

const isKind = (k) => SHARE_KINDS.includes(k);

// Build the share URL that gets emailed/copied. Pair with parseShareUrl().
// A Library document carries editionId + classificationId as query params so the
// redeemed scope can be turned back into the real document route
// (/library/:edition/document/:classification). Course documents keep courseId.
export const buildShareUrl = (hash, scope) => {
  if (!hash || !scope || !isKind(scope.kind) || !scope.id) return null;
  const base = `/share/${encodeURIComponent(hash)}/${scope.kind}/${encodeURIComponent(scope.id)}`;
  const params = [];
  if (scope.courseId) params.push(`courseId=${encodeURIComponent(scope.courseId)}`);
  if (scope.editionId) params.push(`editionId=${encodeURIComponent(scope.editionId)}`);
  if (scope.classificationId) params.push(`classificationId=${encodeURIComponent(scope.classificationId)}`);
  return params.length ? `${base}?${params.join('&')}` : base;
};

// Pull the hash + scope out of a location (or null if it isn't a share-link URL).
// Accepts a location-like {pathname, search} or a full URL/pathname string.
export const parseShareUrl = (location) => {
  const pathname = typeof location === 'string' ? location : location?.pathname || '';
  const search = typeof location === 'string' ? '' : location?.search || '';
  const match = pathname.match(/^\/share\/([^/?#]+)\/([^/?#]+)\/([^/?#]+)/);
  if (!match) return null;
  const [, rawHash, kind, rawId] = match;
  if (!isKind(kind)) return null;
  const decode = (v) => {
    try { return decodeURIComponent(v); } catch { return v; }
  };
  const scope = { kind, id: decode(rawId) };
  const courseMatch = search.match(/[?&]courseId=([^&]+)/);
  if (courseMatch) scope.courseId = decode(courseMatch[1]);
  const editionMatch = search.match(/[?&]editionId=([^&]+)/);
  if (editionMatch) scope.editionId = decode(editionMatch[1]);
  const classificationMatch = search.match(/[?&]classificationId=([^&]+)/);
  if (classificationMatch) scope.classificationId = decode(classificationMatch[1]);
  return { hash: decode(rawHash), scope };
};

// Given a redeemed scope, return the canonical app path the user should land on.
// Scope shape: { kind: 'assessment' | 'task' | 'document', id: string,
//   courseId?: string, editionId?: string, classificationId?: string }
// A Library document lives at /library/:edition/document/:classification, so we
// build that path from editionId + classificationId. The scope.id (the
// edition-document join id) is retained only as the backend shareable id.
export const IN_SCOPE_PATH_FOR = (scope) => {
  if (!scope) return '/';
  switch (scope.kind) {
    case 'assessment':
      return `/reports/assessment/${scope.id}`;
    case 'task':
      return `/tasks/detail/${scope.id}`;
    case 'document':
      if (scope.courseId) return `/courses/${scope.courseId}/document/${scope.id}`;
      if (scope.editionId && scope.classificationId) {
        return `/library/${scope.editionId}/document/${scope.classificationId}`;
      }
      return `/library/${scope.id}`;
    default:
      return '/';
  }
};

// True iff the given pathname is the canonical view for the given scope.
// Used by HashViewGuard to pin the user to the shared content. Assessment +
// document scopes have legitimate sub-routes (e.g. /reports/assessment/:id/scores,
// /photos, /info, /highlights) that the in-page tabs link to — we treat the
// canonical path as a prefix so tab navigation works inside a hashed session.
export const isPathInScope = (pathname, scope) => {
  if (!scope || !pathname) return false;
  const base = IN_SCOPE_PATH_FOR(scope);
  if (pathname === base) return true;
  return pathname.startsWith(`${base}/`);
};
